import { Platform } from '@cart-services/hooks/fp-b2b/store-map'
import { datadogRum } from '@datadog/browser-rum'
import { useApiCampaignActivityInfo } from '@hooks-api/fortune/useApiCampaignActivityInfo'
import { useCallback, useEffect } from 'react'
import { Redirect, Route, Switch, useHistory, useRouteMatch } from 'react-router-dom-v5'
import { AiPurchaseOrderRecommendations } from '../fp-b2b-shared/ai-purchase-order/AiPurchaseOrderRecommendations'
import { CheckoutProvider } from '../fp-b2b-shared/components/CheckoutProvider/CheckoutProvider'
import { ThankyouProvider } from '../fp-b2b-shared/components/ThankyouProvider/ThankyouProvider'
import { Cart } from './cart'
import { StmCartProvider } from './cart/CartProvider'
import { CartFooterAction } from './cart/components/CartFooterAction'
import { CheckoutProcessor, RetryPaymentProcessor } from './checkout'
import { BrandCompactSelector } from './components/BrandCompactSelector/BrandCompactSelector'
import { BrandSelector } from './components/BrandSelector/BrandSelector'
import { DialogAuthChanged } from './components/DialogAuthChanged'
import { Header } from './components/Header/Header'
import { LoaderStm } from './components/LoaderStm'
import { CartFooterDetail } from './components/PageLayout/CartFooterDetail'
import { GoToCartFooterAction } from './components/PageLayout/GoToCartFooterAction'
import { PageFooter } from './components/PageLayout/PageFooter'
import { PageLayout } from './components/PageLayout/PageLayout'
import { QuickOrderFab } from './components/QuickOrderFab/QuickOrderFab'
import { StmRefreshPlatformToken } from './components/StmRefreshPlatformToken'
import { HomePage } from './home/HomePage'
import { useActiveBrands, useStmBrandStore, useStmCampaignCode, useStmQuickOrderStore, useStmRoleGuard } from './hooks'
import { LayoutMyAccount } from './my-account/components/LayoutMyAccount'
import { OrderDetailContainer } from './my-account/order-detail/OrderDetailContainer'
import { Orders } from './my-account/orders'
import { NewProducts } from './new-products'
import { ProductCatalog } from './product-catalog'
import { CatalogFooterAction } from './product-catalog/components/CatalogFooterAction'
import { QuickOrder } from './quick-order/QuickOrder'
import { StmCsvRecommendations } from './quick-order/StmCsvRecommendations'
import { StmQuickOrderProvider } from './quick-order/StmQuickOrderProvider'
import { Thankyou } from './thankyou'
import { clearStmCookies } from './utils'

interface BrandRoutesProps {
  basePath: string
  onLogout: () => void
}

/**
 * 品牌限定路由
 * 確保 StmCartProvider 收到的 campaignId 永遠有值
 */
const BrandRoutes = (props: BrandRoutesProps) => {
  const { basePath, onLogout } = props
  const { currentBrand, setBrand } = useStmBrandStore()
  const activeBrands = useActiveBrands()
  const { campaignCode, campaignId, projectCode, countryCode: brandCountryCode } = useStmCampaignCode()
  const { destructuredData: campaign } = useApiCampaignActivityInfo(campaignCode)
  const currency = campaign?.currency
  const language = campaign?.language

  useEffect(function selectFirstActiveBrandWhenUnset() {
    if (currentBrand) return
    const firstActiveBrand = activeBrands[0]
    if (firstActiveBrand) setBrand(firstActiveBrand.campaignCode)
  }, [currentBrand, activeBrands, setBrand])

  if (!projectCode || !brandCountryCode || !campaignId || !campaignCode) {
    return <LoaderStm />
  }

  return (
    <StmCartProvider
      basePath={basePath}
      campaignCode={campaignCode}
      campaignId={campaignId}
      projectCode={projectCode}
      countryCode={brandCountryCode}
      currency={currency}
    >
      <Switch>
        <Route path={`${basePath}/product-catalog`} exact>
          <PageLayout
            brandSelector={<BrandSelector />}
            footer={<PageFooter detail={<CartFooterDetail />} actionButton={<CatalogFooterAction basePath={basePath} />} floatingAction={<QuickOrderFab basePath={basePath} />} />}
          >
            <ProductCatalog />
          </PageLayout>
        </Route>
        <Route path={`${basePath}/cart`} exact>
          <PageLayout
            brandSelector={<BrandCompactSelector showCartCount />}
            footer={<PageFooter detail={<CartFooterDetail />} actionButton={<CartFooterAction />} floatingAction={<QuickOrderFab basePath={basePath} />} />}
          >
            <Cart />
          </PageLayout>
        </Route>
        <Route path={`${basePath}/checkout`} exact>
          {language && currency
          && (
            <CheckoutProvider
              platform={Platform.STM}
              basePath={basePath}
              projectCode={projectCode}
              currency={currency}
              countryCode={brandCountryCode}
              language={language}
              campaignId={campaignId}
              campaignCode={campaignCode}
              env={import.meta.env.VITE_APP_ENV === 'production' ? 'production' : 'sandbox'}
              fallbackUrl={`${basePath}/product-catalog`}
            >
              <CheckoutProcessor />
            </CheckoutProvider>
          )}
        </Route>
        <Route path={`${basePath}/retry-payment/:transactionId`} exact>
          {language && currency
          && (
            <CheckoutProvider
              platform={Platform.STM}
              basePath={basePath}
              projectCode={projectCode}
              currency={currency}
              countryCode={brandCountryCode}
              language={language}
              campaignId={campaignId}
              campaignCode={campaignCode}
              env={import.meta.env.VITE_APP_ENV === 'production' ? 'production' : 'sandbox'}
            >
              <RetryPaymentProcessor />
            </CheckoutProvider>
          )}
        </Route>
        <Route path={`${basePath}/thankyou/:transactionId`} exact>
          <ThankyouProvider
            projectCode={projectCode}
            countryCode={brandCountryCode}
            currency={currency}
            campaignId={campaignId}
            platform={Platform.STM}
          >
            <Thankyou basePath={basePath} />
          </ThankyouProvider>
        </Route>
        <Route path={`${basePath}/my-account/order`} exact>
          <LayoutMyAccount campaignCode={campaignCode} basePath={basePath} onLogout={onLogout}>
            <Orders basePath={basePath} />
          </LayoutMyAccount>
        </Route>
        <Route path={`${basePath}/my-account/order-detail/:transactionId`} exact>
          <OrderDetailContainer basePath={basePath} onLogout={onLogout} />
        </Route>
        <Route path="*">
          <Redirect to={basePath} />
        </Route>
      </Switch>
    </StmCartProvider>
  )
}

export const AuthRoutes = () => {
  const { path, url } = useRouteMatch()
  const history = useHistory()
  const { setBrands, setBrand, setCountryCode, setUserRole } = useStmBrandStore()
  const handleFullLogout = useCallback(() => {
    clearStmCookies()
    datadogRum.clearUser()
    setBrand(null)
    setBrands([])
    setCountryCode(null)
    setUserRole(null)
    useStmQuickOrderStore.getState().reset()
    history.push(`${url}/login`)
  }, [url, setBrand, setBrands, setCountryCode, setUserRole, history])

  useStmRoleGuard()

  return (
    <>
      <StmRefreshPlatformToken />
      <DialogAuthChanged onConfirm={handleFullLogout} />
      <Header onLogout={handleFullLogout} />
      <Switch>
        {/* 通用路由：currentBrand 可為 null */}
        <Route path={path} exact>
          <PageLayout
            footer={<PageFooter actionButton={<GoToCartFooterAction basePath={url} />} floatingAction={<QuickOrderFab basePath={url} />} />}
          >
            <HomePage basePath={url} />
          </PageLayout>
        </Route>
        <Route path={`${path}/new-products`} exact>
          <PageLayout
            brandSelector={<BrandSelector allowDeselect />}
            footer={<PageFooter actionButton={<GoToCartFooterAction basePath={url} />} floatingAction={<QuickOrderFab basePath={url} />} />}
          >
            <NewProducts basePath={url} />
          </PageLayout>
        </Route>
        <Route path={`${path}/quick-order/recommendations/csv`} exact>
          <StmQuickOrderProvider basePath={url}>
            <StmCsvRecommendations />
          </StmQuickOrderProvider>
        </Route>
        <Route path={`${path}/quick-order/recommendations`} exact>
          <StmQuickOrderProvider basePath={url}>
            <AiPurchaseOrderRecommendations />
          </StmQuickOrderProvider>
        </Route>
        <Route path={`${path}/quick-order`} exact>
          <StmQuickOrderProvider basePath={url}>
            <QuickOrder />
          </StmQuickOrderProvider>
        </Route>
        
        {/* 品牌限定路由 */}
        <Route path={`${path}/(product-catalog|cart|checkout|retry-payment|thankyou|my-account)`}>
          <BrandRoutes basePath={url} onLogout={handleFullLogout} />
        </Route>
        <Route path="*">
          <Redirect to={url} />
        </Route>
      </Switch>
    </>
  )
}
